/**
 * Timezone Helper Utilities for Account Dashboard
 * Handles Pacific Time conversions and deadline calculations
 */

/**
 * Parses OrderGroove order `place` from REST ("YYYY-MM-DD HH:mm:ss") or GraphQL ISO-8601 (with Z or offset).
 * @param {string} placeDate
 * @returns {Date|null}
 */
export function parseOgPlaceDateUtc(placeDate) {
  if (placeDate == null || placeDate === '') return null;
  const s = String(placeDate).trim();
  if (!s) return null;
  if (/\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s.replace(' ', 'T') + 'Z');
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Gets the current time in Pacific timezone
 * @returns {Date} Current date/time in Pacific timezone
 */
export function getCurrentPacificTime() {
  const now = new Date();
  // Create a date string in Pacific timezone
  const pacificTimeString = now.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
  });
  return new Date(pacificTimeString);
}

/**
 * Converts any date to Pacific timezone
 * @param {Date|string} date - Date object or ISO string
 * @returns {Date} Date converted to Pacific timezone
 */
export function toPacificTime(date) {
  const inputDate = date instanceof Date ? date : new Date(date);
  const pacificTimeString = inputDate.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
  });
  return new Date(pacificTimeString);
}

/**
 * Calculates the edit deadline: 8:00 PM Pacific Time on the day before place date
 * Uses a reliable method to convert Pacific Time to UTC
 * @param {string} placeDate - Order place date in format 'YYYY-MM-DD HH:MM:SS' (UTC)
 * @returns {Date} Edit deadline as Date object
 */
export function calculateEditDeadline(placeDate) {
  const placeDateUTC = parseOgPlaceDateUtc(placeDate);
  if (!placeDateUTC || Number.isNaN(placeDateUTC.getTime())) return null;

  // Subtract one day from the UTC date (not the Pacific date!)
  // Example: Nov 22 UTC -> Nov 21 UTC (this is the deadline day)
  const deadlineUTC = new Date(placeDateUTC);
  deadlineUTC.setUTCDate(deadlineUTC.getUTCDate() - 1);

  // Get the deadline date components in UTC
  const deadlineYear = deadlineUTC.getUTCFullYear();
  const deadlineMonth = String(deadlineUTC.getUTCMonth() + 1).padStart(2, '0');
  const deadlineDay = String(deadlineUTC.getUTCDate()).padStart(2, '0');

  // Here's the key: We need to find what UTC time corresponds to "8pm Pacific" on this date
  // We'll do this by creating a test date and using toLocaleString to find the offset

  // Create an arbitrary UTC time on the deadline date
  const testUTC = new Date(`${deadlineYear}-${deadlineMonth}-${deadlineDay}T00:00:00Z`);

  // See what time this is in Pacific
  const ptTimeStr = testUTC.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });

  // Parse the hour
  const ptHour = parseInt(ptTimeStr.split(':')[0]);

  // Calculate the offset (how many hours behind UTC is Pacific at this date)
  // If UTC midnight (0:00) shows as 16:00 in PT, then PT is 8 hours behind (PST)
  // If UTC midnight (0:00) shows as 17:00 in PT, then PT is 7 hours behind (PDT)
  const offsetHours = ptHour <= 12 ? ptHour : ptHour - 24;
  const pacificOffsetHours = -offsetHours; // Negative because PT is behind UTC

  // Now calculate: what UTC time equals 8pm Pacific?
  // 8pm Pacific + offset = UTC time
  // Example: 8pm PST (UTC-8) = 8pm + 8 hours = 4am UTC next day
  const deadlineUTCHour = 20 + pacificOffsetHours;

  // Create the actual deadline
  const deadline = new Date(`${deadlineYear}-${deadlineMonth}-${deadlineDay}T00:00:00Z`);
  deadline.setUTCHours(deadlineUTCHour, 0, 0, 0);

  return deadline;
}

/**
 * Calculates the processing threshold: 3 hours before the place date
 * Note: Currently unused but kept for potential future features
 * @param {string} placeDate - Order place date in format 'YYYY-MM-DD HH:MM:SS' (UTC)
 * @returns {Date} Processing threshold as Date object
 */
export function calculateProcessingThreshold(placeDate) {
  const placeDateUTC = parseOgPlaceDateUtc(placeDate);
  if (!placeDateUTC || Number.isNaN(placeDateUTC.getTime())) return null;
  return new Date(placeDateUTC.getTime() - 3 * 60 * 60 * 1000);
}

/**
 * Checks if current time is before the edit deadline
 * @param {Date} editDeadline - The edit deadline
 * @returns {boolean} True if still within edit window
 */
export function isBeforeEditDeadline(editDeadline) {
  if (!editDeadline) return false;
  return new Date() < editDeadline;
}

/**
 * Checks if current time is in the "processing soon" window
 * This is the 2-hour buffer between 8:00 PM PT and 10:00 PM PT the day before processing
 * @param {Date} editDeadline - The edit deadline (8pm PT day before)
 * @param {string} placeDate - Order place date (required for validation, not used in calculation)
 * @param {number} orderStatus - The order status (6 = processing)
 * @returns {boolean} True if in the 2-hour buffer window (8 PM - 10 PM PT)
 */
export function isProcessingSoon(editDeadline, placeDate, orderStatus) {
  if (!editDeadline || !placeDate || orderStatus === 6) return false;
  const now = new Date();

  // Processing starts at 10:00 PM PT (2 hours after the 8:00 PM edit deadline)
  const processingStartTime = new Date(editDeadline.getTime() + (2 * 60 * 60 * 1000));

  return now >= editDeadline && now < processingStartTime;
}

/**
 * Checks if order is currently processing
 * @param {number} orderStatus - The order status (6 = processing)
 * @returns {boolean} True if order is processing
 */
export function isProcessing(orderStatus) {
  return orderStatus === 6;
}

/**
 * Formats a date for display with Pacific timezone
 * @param {Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDatePacific(date, options = {}) {
  if (!date) return '';

  const defaultOptions = {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  return new Date(date).toLocaleString('en-US', { ...defaultOptions, ...options });
}

/**
 * Formats just the date part (no time) for display
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDateOnly(date) {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
